/**
 * The assistant's room tabs (TMMAPP-0178 items 1–2, the Operator's mock-up of 2026-10-27): large boxes
 * above the chat that switch between a shared conversation's **Shared room** and the person's own
 * **Private room**. They replace TMMAPP-0153's Step into my breakout room / Return to shared session
 * pills and visual contract v2 §11's nested breakout row, on this screen only.
 *
 * - Each tab is a rounded box on `surface`: the label in capitals (the heading face at 600), with the
 *   avatars under it at the end: the room's people (up to three, then "+n") and the assistant's mark.
 * - The selected tab has the `brandBoundary` border, doubled; the other a `line` border and a `text2`
 *   label. Tokens only (pairs in `tests/contrast.ts`).
 * - A `tablist` named "Rooms in {name}"; each box a `tab` with `aria-selected`. The arrow keys (and Home
 *   and End) move focus between them; Enter or Space opens one (a route change, the page's). The chat
 *   below is the `tabpanel` (`panelId`).
 * - About 230 px each at `md` and up; at `xs` they share the width equally. A longer label wraps.
 */
import { useRef, type KeyboardEvent } from 'react';
import { Avatar, Box, ButtonBase, Stack, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AssistantSparkleNavIcon } from '@/components/icons';
import { formatNumber } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { initialOf } from '@/shell/initials';
import { tintFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';

/** The tabs' avatars. */
const AVATAR = 22;
/** Up to this many people show on a tab, then "+n". */
const MAX_PEOPLE = 3;

/** One person on a tab: a member's served name, or the person themself (`mine`). */
export interface RoomPerson {
  name: string | null;
  mine: boolean;
}

export interface RoomTab {
  key: 'shared' | 'private';
  /** The tab's own id, for the tabpanel's `aria-labelledby`. */
  id: string;
  selected: boolean;
  people: readonly RoomPerson[];
  /** Opens this room (a route change); never called for the selected tab. */
  onOpen: () => void;
  /** Opening it is in flight (the private room being made). */
  busy?: boolean | undefined;
}

interface RoomTabsProps {
  /** The conversation's name, for the tablist's name. */
  name: string;
  tabs: readonly RoomTab[];
  /** The chat below (the `tabpanel`). */
  panelId: string;
}

export function RoomTabs({ name, tabs, panelId }: RoomTabsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const locale = useLocale();
  const me = useMe();
  const peach = tintFor('peach', tokens);
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const rtl = theme.direction === 'rtl';

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const current = refs.current.findIndex((node) => node === document.activeElement);
    if (current < 0) return;
    const last = tabs.length - 1;
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = rtl ? 'ArrowRight' : 'ArrowLeft';
    const next =
      event.key === forward
        ? current === last
          ? 0
          : current + 1
        : event.key === backward
          ? current === 0
            ? last
            : current - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    refs.current[next]?.focus();
  }

  const avatar = {
    width: AVATAR,
    height: AVATAR,
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 600,
    border: '2px solid',
    borderColor: tokens.surface,
    boxSizing: 'content-box',
  } as const;

  function people(list: readonly RoomPerson[]) {
    const shown = list.slice(0, MAX_PEOPLE);
    const more = list.length - shown.length;
    return (
      <Stack
        direction="row"
        aria-hidden
        sx={{ alignSelf: 'flex-end', alignItems: 'center' }}
        data-room-tab-people=""
      >
        {shown.map((person, index) => (
          <Avatar
            key={`${person.name ?? ''}-${index}`}
            sx={{
              ...avatar,
              marginInlineStart: index === 0 ? 0 : '-6px',
              bgcolor: person.mine ? tokens.brand : peach.soft,
              color: person.mine ? tokens.brandInk : tokens.text,
            }}
            data-room-tab-person=""
          >
            {initialOf(person.mine ? me.user.displayName : personName(person.name, t))}
          </Avatar>
        ))}
        {more > 0 && (
          <Avatar
            sx={{
              ...avatar,
              marginInlineStart: '-6px',
              bgcolor: tokens.surface2,
              color: tokens.text,
            }}
            data-room-tab-more=""
          >
            {`+${formatNumber(more, locale)}`}
          </Avatar>
        )}
        <Avatar
          sx={{
            ...avatar,
            marginInlineStart: shown.length + (more > 0 ? 1 : 0) === 0 ? 0 : '-6px',
            bgcolor: tokens.successSoft,
            color: tokens.successText,
          }}
          data-room-tab-assistant=""
        >
          <AssistantSparkleNavIcon size={13} />
        </Avatar>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      role="tablist"
      aria-label={t('assistant.rooms.label', { name })}
      onKeyDown={onKeyDown}
      data-room-tabs=""
      sx={{ gap: 1.5, flexWrap: 'nowrap' }}
    >
      {tabs.map((tab, index) => (
        <ButtonBase
          key={tab.key}
          ref={(node: HTMLButtonElement | null) => {
            refs.current[index] = node;
          }}
          id={tab.id}
          role="tab"
          aria-selected={tab.selected}
          aria-controls={panelId}
          aria-busy={tab.busy === true || undefined}
          tabIndex={tab.selected ? 0 : -1}
          onClick={() => {
            if (!tab.selected) tab.onOpen();
          }}
          data-room-tab={tab.key}
          sx={{
            flex: { xs: '1 1 0', md: '0 0 230px' },
            maxWidth: { xs: 'calc(50% - 6px)', md: 230 },
            minWidth: 0,
            minHeight: 64,
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            gap: 1,
            p: '10px 12px',
            borderRadius: '14px',
            border: '1px solid',
            textAlign: 'start',
            bgcolor: tokens.surface,
            ...(tab.selected
              ? {
                  borderColor: tokens.brandBoundary,
                  boxShadow: `inset 0 0 0 1px ${tokens.brandBoundary}`,
                  color: tokens.text,
                }
              : {
                  borderColor: tokens.line,
                  color: tokens.text2,
                  '&:hover': { bgcolor: tokens.hover },
                }),
            '&.Mui-focusVisible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
          }}
        >
          <Box
            component="span"
            data-room-tab-label=""
            sx={{
              fontFamily: (current) => current.typography.h4.fontFamily,
              fontSize: 13,
              lineHeight: '18px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              overflowWrap: 'anywhere',
            }}
          >
            {t(tab.key === 'shared' ? 'assistant.rooms.shared' : 'assistant.rooms.private')}
          </Box>
          {people(tab.people)}
        </ButtonBase>
      ))}
    </Stack>
  );
}
